/**
 * Difficulty (2026-10-28 difficulty pass; Bennett: "The game might be getting too easy"). Three choices the host
 * makes in the lobby, all of them on top of the waves' own ramp:
 *
 * - The ramp (every difficulty): waves 1-5 stay as they were (they teach the elements and the first boss), and from
 *   wave 6 every wave adds enemy HP, damage and bodies and spawns them faster (`waveRamp`).
 * - Elites (`ELITE`): from a difficulty's `eliteFrom` wave a share of the bodies spawn elite: twice the HP, harder
 *   hits, a little faster, and immune to one element (`ELITE.resists`), drawn on them, so the one combo you lean on
 *   stops working on that one and you have to reach for another.
 * - Hard and Brutal multiply HP, damage, counts, spawn pacing and the bosses on top, from wave 1, start elites
 *   earlier and shorten the bosses' tells.
 *
 * Story mode stays a light tutorial on Normal (only its boss is tougher); Hard and Brutal scale its rooms too.
 * Capture the flag has no enemies and ignores all of this: PvP numbers live in PVP.
 */

import type { Element } from './elements.js';
import type { EnemyKind } from './enemies.js';
import { SPAWN_INTERVAL } from './constants.js';

export type Difficulty = 'normal' | 'hard' | 'brutal';

export const DIFFICULTIES: readonly Difficulty[] = ['normal', 'hard', 'brutal'];

export function isDifficulty(x: unknown): x is Difficulty {
  return typeof x === 'string' && (DIFFICULTIES as readonly string[]).includes(x);
}

export interface DifficultyDef {
  label: string;
  /** One line for the lobby. */
  blurb: string;
  /** Enemy HP, enemy damage and body counts, x the wave's own. */
  hp: number;
  dmg: number;
  count: number;
  /** x the seconds between spawns (lower is faster). */
  spawn: number;
  /** First wave with elites, and extra elite chance on top of the wave's. */
  eliteFrom: number;
  eliteBonus: number;
  /** The bosses' HP, and x their tell and wind-up times (lower is less warning). */
  bossHp: number;
  tell: number;
}

export const DIFFICULTY: Readonly<Record<Difficulty, DifficultyDef>> = {
  normal: { label: 'Normal', blurb: 'Gentle first waves, then it ramps hard.', hp: 1, dmg: 1, count: 1, spawn: 1, eliteFrom: 8, eliteBonus: 0, bossHp: 1, tell: 1 },
  hard: { label: 'Hard', blurb: 'Tougher, faster, more of them, elites from wave 5.', hp: 1.3, dmg: 1.2, count: 1.2, spawn: 0.85, eliteFrom: 5, eliteBonus: 0.06, bossHp: 1.3, tell: 0.85 },
  brutal: { label: 'Brutal', blurb: 'For the wizard who has seen it all. Bring friends.', hp: 1.55, dmg: 1.4, count: 1.4, spawn: 0.75, eliteFrom: 3, eliteBonus: 0.12, bossHp: 1.6, tell: 0.75 },
};

/**
 * The ramp after the friendly opening: k waves past wave 5, HP, damage and bodies grow by `a`k + `b`k^2 (gentle at
 * wave 6-7, steep by 15-20: a straight line put the whole jump at wave 7, where pairs of bots started wiping), and
 * each wave takes `spawn` seconds off the gap between spawns, down to `spawnFloor`.
 */
export const RAMP = {
  from: 6,
  hp: { a: 0.05, b: 0.004 },
  dmg: { a: 0.02, b: 0.002 },
  count: { a: 0.03, b: 0.002 },
  spawn: 0.012,
  spawnFloor: 0.22,
} as const;

/** The ramp's multipliers for a wave: all 1 (and the base spawn gap) through wave 5. */
export function waveRamp(wave: number): { hp: number; dmg: number; count: number; spawnInterval: number } {
  const k = Math.max(0, Math.floor(wave) - RAMP.from + 1);
  const grow = (c: { a: number; b: number }): number => 1 + c.a * k + c.b * k * k;
  return {
    hp: grow(RAMP.hp),
    dmg: grow(RAMP.dmg),
    count: grow(RAMP.count),
    spawnInterval: Math.max(RAMP.spawnFloor, SPAWN_INTERVAL - RAMP.spawn * k),
  };
}

/**
 * Elites: `hp` and `dmg` x a normal body's, `speed` x its walk; immune to one of `resists` (never Life, which the
 * undead need, and never Earth: a rock always answers). Chance per body: `chance` at the difficulty's first elite
 * wave, + `perWave` each wave after, at most `max`.
 */
export const ELITE = {
  hp: 2,
  dmg: 1.3,
  speed: 1.1,
  chance: 0.1,
  perWave: 0.02,
  max: 0.4,
  resists: ['fire', 'steam', 'arcane', 'lightning', 'cold'] as readonly Element[],
  /**
   * A kind's weakness is never its elite's resistance: a fire-proof tree spirit would undo the lesson it teaches.
   * A fire-proof bomber can't burn, so its held bomb could never be set off.
   */
  never: { treespirit: ['fire'], troll: ['fire'], orc: ['lightning'], bomber: ['fire'] } as Readonly<Partial<Record<EnemyKind, readonly Element[]>>>,
} as const;

/** The elements an elite of `kind` may resist. */
export function eliteResists(kind: EnemyKind): readonly Element[] {
  const never = ELITE.never[kind] ?? [];
  return ELITE.resists.filter((el) => !never.includes(el));
}

/** The share of a wave's bodies that spawn elite (0 before the difficulty's first elite wave). */
export function eliteChance(wave: number, d: Difficulty): number {
  const def = DIFFICULTY[d];
  const n = Math.floor(wave);
  if (n < def.eliteFrom) return 0;
  return Math.min(ELITE.max, ELITE.chance + ELITE.perWave * (n - def.eliteFrom) + def.eliteBonus);
}

/** Hard and Brutal reach their full numbers at this wave: the waves before it get a share (wave / PHASE_IN). */
export const PHASE_IN = 5;

/**
 * Everything a wave's spawner needs: HP, damage and count multipliers, the spawn gap, the elite chance. A
 * difficulty's extra phases in over the first PHASE_IN waves, so even Brutal opens on something you can learn from.
 */
export function waveTuning(wave: number, d: Difficulty): { hp: number; dmg: number; count: number; spawnInterval: number; elite: number } {
  const r = waveRamp(wave);
  const def = DIFFICULTY[d];
  const k = Math.min(1, Math.max(1, Math.floor(wave)) / PHASE_IN);
  const part = (x: number): number => 1 + (x - 1) * k;
  return { hp: r.hp * part(def.hp), dmg: r.dmg * part(def.dmg), count: r.count * part(def.count), spawnInterval: r.spawnInterval * part(def.spawn), elite: eliteChance(wave, d) };
}

/** Story rooms on this difficulty: Normal leaves the act as it is (a light tutorial); Hard and Brutal scale it. */
export function storyTuning(d: Difficulty): { hp: number; dmg: number; count: number; elite: number } {
  const def = DIFFICULTY[d];
  return { hp: def.hp, dmg: def.dmg, count: def.count, elite: d === 'normal' ? 0 : ELITE.chance + def.eliteBonus };
}
